// src/pages/ParametresPage.jsx
import React, { useState, useEffect } from 'react';
import { getDocs, collection, doc, setDoc, deleteDoc, updateDoc, addDoc, writeBatch } from "firebase/firestore";
import { db } from '../firebase/config';
import PageWrapper from '../layout/PageWrapper';
import { 
  Settings, 
  Palette, 
  Plus, 
  Trash2, 
  Save, 
  Eye, 
  User, 
  FileText, 
  Check, 
  X,
  Phone,
  Mail,
  MapPin,
  Edit,
  ChevronUp,
  ChevronDown,
  CheckSquare,
  Square
} from 'lucide-react';
import { TbSettings } from "react-icons/tb";

// Import des données des types d'actes
import { champsParCategorie } from '../config/actesConfig';

const ParametresPage = () => {
  const [activeTab, setActiveTab] = useState('tags');
  const [tagColors, setTagColors] = useState([]);
  const [intervenants, setIntervenants] = useState([]);
  const [typesActes, setTypesActes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // États pour les tags
  const [newTag, setNewTag] = useState({ nom: '', codeHex: '#2563eb', ordre: 1 });
  const [editingTag, setEditingTag] = useState(null);
  const [nextId, setNextId] = useState(1);
  
  // États pour les intervenants
  const [newIntervenant, setNewIntervenant] = useState({
    nom: '',
    prenom: '',
    type: 'Vétérinaire',
    telephone: '',
    email: '',
    adresse: '',
    actif: true
  });
  const [editingIntervenant, setEditingIntervenant] = useState(null);
  
  // États pour les types d'actes
  const [expandedCategories, setExpandedCategories] = useState({});
  const [categoriesColors, setCategoriesColors] = useState({});
  const [recentColors, setRecentColors] = useState([]);
  const [showColorPicker, setShowColorPicker] = useState(null);

  useEffect(() => {
    // Charger les couleurs récentes du localStorage
    const savedRecentColors = localStorage.getItem('recentActeColors');
    if (savedRecentColors) {
      setRecentColors(JSON.parse(savedRecentColors));
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'tags') {
      loadTagColors();
    } else if (activeTab === 'intervenants') {
      loadIntervenants();
    } else if (activeTab === 'actes') {
      loadTypesActes();
    }
  }, [activeTab]);

  const loadTagColors = async () => {
    try {
      setLoading(true);
      const tagColorsSnap = await getDocs(collection(db, "tagCouleursAnimaux"));
      let colorsData = [];
      let maxId = 0;
      
      tagColorsSnap.forEach(doc => {
        const data = doc.data();
        const tagData = {
          id: parseInt(doc.id) || 0,
          nom: data.nom || '',
          codeHex: data.codeHex || '#000000',
          ordre: data.ordre || parseInt(doc.id) || 0
        };
        colorsData.push(tagData);
        maxId = Math.max(maxId, tagData.id);
      });

      colorsData.sort((a, b) => a.ordre - b.ordre || a.id - b.id);
      setTagColors(colorsData);
      setNextId(maxId + 1);
    } catch (error) {
      console.error('Erreur lors du chargement des couleurs:', error);
      showMessage('error', 'Erreur lors du chargement des couleurs');
    } finally {
      setLoading(false);
    }
  };

  const loadIntervenants = async () => {
    try {
      setLoading(true);
      const intervenantsSnap = await getDocs(collection(db, "intervenants"));
      const intervenantsData = intervenantsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setIntervenants(intervenantsData.sort((a, b) => a.nom.localeCompare(b.nom)));
    } catch (error) {
      console.error('Erreur lors du chargement des intervenants:', error);
      showMessage('error', 'Erreur lors du chargement des intervenants');
    } finally {
      setLoading(false);
    }
  };

  const loadTypesActes = async () => {
    try {
      setLoading(true);
      const typesActesSnap = await getDocs(collection(db, "typesActes"));
      const typesActesData = typesActesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Grouper par catégorie
      const grouped = typesActesData.reduce((acc, acte) => {
        if (!acc[acte.typeCategorie]) {
          acc[acte.typeCategorie] = [];
        }
        acc[acte.typeCategorie].push(acte);
        return acc;
      }, {});
      
      // Trier les actes dans chaque catégorie
      Object.keys(grouped).forEach(cat => {
        grouped[cat].sort((a, b) => a.ordre - b.ordre || a.nomActe.localeCompare(b.nomActe));
      });
      
      setTypesActes(grouped);
      
      // Charger les couleurs de catégories depuis localStorage
      const savedCategoryColors = localStorage.getItem('categoriesColors');
      if (savedCategoryColors) {
        setCategoriesColors(JSON.parse(savedCategoryColors));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des types d\'actes:', error);
      showMessage('error', 'Erreur lors du chargement des types d\'actes');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  // Fonction pour ajouter une couleur aux couleurs récentes
  const addToRecentColors = (color) => {
    const newRecentColors = [color, ...recentColors.filter(c => c !== color)].slice(0, 5);
    setRecentColors(newRecentColors);
    localStorage.setItem('recentActeColors', JSON.stringify(newRecentColors));
  };

  // === GESTION DES TAGS ===
  const handleSaveTag = async (tagData) => {
    if (!tagData.nom.trim()) {
      showMessage('error', 'Le nom du tag est obligatoire');
      return;
    }

    const existingTag = tagColors.find(tag => 
      tag.nom.toLowerCase() === tagData.nom.toLowerCase() && 
      tag.id !== editingTag?.id
    );
    
    if (existingTag) {
      showMessage('error', 'Un tag avec ce nom existe déjà');
      return;
    }

    try {
      setLoading(true);
      
      let docId, saveData;
      
      if (editingTag) {
        docId = editingTag.id.toString();
        saveData = {
          id: editingTag.id,
          nom: tagData.nom.trim(),
          codeHex: tagData.codeHex,
          ordre: tagData.ordre
        };
      } else {
        docId = nextId.toString();
        saveData = {
          id: nextId,
          nom: tagData.nom.trim(),
          codeHex: tagData.codeHex,
          ordre: nextId
        };
      }
      
      await setDoc(doc(db, "tagCouleursAnimaux", docId), saveData);

      showMessage('success', editingTag ? 'Tag modifié avec succès' : 'Tag ajouté avec succès');
      setNewTag({ nom: '', codeHex: '#2563eb', ordre: nextId + 1 });
      setEditingTag(null);
      await loadTagColors();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showMessage('error', 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tagId, tagNom) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le tag "${tagNom}" ?`)) {
      return;
    }

    try {
      setLoading(true);
      await deleteDoc(doc(db, "tagCouleursAnimaux", tagId.toString()));
      showMessage('success', 'Tag supprimé avec succès');
      await loadTagColors();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showMessage('error', 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  // === GESTION DES INTERVENANTS ===
  const handleSaveIntervenant = async () => {
    if (!newIntervenant.nom.trim() || !newIntervenant.prenom.trim()) {
      showMessage('error', 'Le nom et le prénom sont obligatoires');
      return;
    }

    try {
      setLoading(true);
      
      if (editingIntervenant) {
        await updateDoc(doc(db, "intervenants", editingIntervenant.id), {
          ...newIntervenant,
          updatedAt: new Date().toISOString()
        });
        showMessage('success', 'Intervenant modifié avec succès');
      } else {
        await addDoc(collection(db, "intervenants"), {
          ...newIntervenant,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        showMessage('success', 'Intervenant ajouté avec succès');
      }
      
      setNewIntervenant({
        nom: '',
        prenom: '',
        type: 'Vétérinaire',
        telephone: '',
        email: '',
        adresse: '',
        actif: true
      });
      setEditingIntervenant(null);
      await loadIntervenants();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showMessage('error', 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleEditIntervenant = (intervenant) => {
    setEditingIntervenant(intervenant);
    setNewIntervenant({
      nom: intervenant.nom,
      prenom: intervenant.prenom,
      type: intervenant.type,
      telephone: intervenant.telephone || '',
      email: intervenant.email || '',
      adresse: intervenant.adresse || '',
      actif: intervenant.actif !== false
    });
  };

  const handleDeleteIntervenant = async (id, nom, prenom) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${prenom} ${nom} ?`)) {
      return;
    }

    try {
      setLoading(true);
      await deleteDoc(doc(db, "intervenants", id));
      showMessage('success', 'Intervenant supprimé avec succès');
      await loadIntervenants();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showMessage('error', 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  // === GESTION DES TYPES D'ACTES ===
  const handleToggleActe = async (acteId, currentStatus) => {
    try {
      await updateDoc(doc(db, "typesActes", acteId), {
        actif: !currentStatus,
        updatedAt: new Date().toISOString()
      });
      await loadTypesActes();
      showMessage('success', 'Statut de l\'acte modifié');
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      showMessage('error', 'Erreur lors de la modification');
    }
  };

  const handleUpdateActeColor = async (acteId, color) => {
    try {
      await updateDoc(doc(db, "typesActes", acteId), {
        couleur: color,
        updatedAt: new Date().toISOString()
      });
      addToRecentColors(color);
      await loadTypesActes();
    } catch (error) {
      console.error('Erreur lors de la modification de la couleur:', error);
    }
  };

  const handleUpdateCategoryColor = async (categorie, color) => {
    try {
      setLoading(true);
      const batch = writeBatch(db);
      
      // Mettre à jour tous les actes de la catégorie
      const actesCategorie = typesActes[categorie] || [];
      actesCategorie.forEach(acte => {
        const acteRef = doc(db, "typesActes", acte.id);
        batch.update(acteRef, {
          couleur: color,
          updatedAt: new Date().toISOString()
        });
      });
      
      await batch.commit();
      
      // Sauvegarder la couleur de catégorie
      const newCategoriesColors = { ...categoriesColors, [categorie]: color };
      setCategoriesColors(newCategoriesColors);
      localStorage.setItem('categoriesColors', JSON.stringify(newCategoriesColors));
      
      addToRecentColors(color);
      showMessage('success', `Couleur de la catégorie ${categorie} mise à jour`);
      await loadTypesActes();
    } catch (error) {
      console.error('Erreur lors de la modification de la couleur de catégorie:', error);
      showMessage('error', 'Erreur lors de la modification de la couleur');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCategory = async (categorie, currentAllActive) => {
    try {
      setLoading(true);
      const batch = writeBatch(db);
      
      // Activer ou désactiver tous les actes de la catégorie
      const actesCategorie = typesActes[categorie] || [];
      actesCategorie.forEach(acte => {
        const acteRef = doc(db, "typesActes", acte.id);
        batch.update(acteRef, {
          actif: !currentAllActive,
          updatedAt: new Date().toISOString()
        });
      });
      
      await batch.commit();
      showMessage('success', `Catégorie ${categorie} ${!currentAllActive ? 'activée' : 'désactivée'}`);
      await loadTypesActes();
    } catch (error) {
      console.error('Erreur lors de la modification de la catégorie:', error);
      showMessage('error', 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateActeOrdre = async (acteId, direction) => {
    // TODO: Implémenter la logique de réorganisation
    showMessage('info', 'Fonctionnalité de réorganisation à venir');
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const presetColors = [
    '#2563eb', '#ef4444', '#22c55e', '#eab308', '#f97316',
    '#8b5cf6', '#ec4899', '#06b6d4', '#6b7280', '#111827'
  ];

  const typesIntervenants = [
    'Vétérinaire',
    'Maréchal',
    'Ostéopathe',
    'Dentiste',
    'Comportementaliste',
    'Nutritionniste',
    'Kinésithérapeute',
    'Masseur',
    'Entraîneur',
    'Autre'
  ];

  const tabs = [
    { id: 'tags', label: 'Couleurs Tags', icon: Palette },
    { id: 'intervenants', label: 'Intervenants', icon: User },
    { id: 'actes', label: 'Types d\'actes', icon: FileText },
    { id: 'autres', label: 'Autres', icon: Settings }
  ];

  // Composant pour le sélecteur de couleur avec couleurs récentes
  const ColorPicker = ({ value, onChange, id }) => {
    const isOpen = showColorPicker === id;
    
    return (
      <div className="relative">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowColorPicker(isOpen ? null : id)}
            className="w-8 h-8 border-2 border-gray-300 rounded cursor-pointer hover:border-gray-400"
            style={{ backgroundColor: value }}
            title="Choisir une couleur"
          />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-20 px-2 py-1 text-xs border border-gray-300 rounded"
          />
        </div>
        
        {isOpen && (
          <div className="absolute z-10 mt-2 p-3 bg-white border rounded-lg shadow-lg">
            <input
              type="color"
              value={value}
              onChange={(e) => {
                onChange(e.target.value);
                addToRecentColors(e.target.value);
              }}
              className="w-32 h-32 border border-gray-300 rounded cursor-pointer mb-2"
            />
            
            {recentColors.length > 0 && (
              <>
                <div className="text-xs text-gray-600 mb-1">Couleurs récentes:</div>
                <div className="flex gap-1">
                  {recentColors.map((color, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        onChange(color);
                        setShowColorPicker(null);
                      }}
                      className="w-6 h-6 border border-gray-300 rounded hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </>
            )}
            
            <div className="text-xs text-gray-600 mb-1 mt-2">Couleurs prédéfinies:</div>
            <div className="flex flex-wrap gap-1">
              {presetColors.map((color) => (
                <button
                  key={color}
                  onClick={() => {
                    onChange(color);
                    addToRecentColors(color);
                    setShowColorPicker(null);
                  }}
                  className="w-6 h-6 border border-gray-300 rounded hover:scale-110 transition-transform"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageWrapper icon={TbSettings} title="Paramètres">
      {message.text && (
        <div className={`mb-4 p-3 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : message.type === 'error'
            ? 'bg-red-50 text-red-800 border border-red-200'
            : 'bg-gray-50 text-gray-800 border border-gray-200'
        }`}>
          {message.text}
        </div>
      )}

      <div className="mb-6">
        <nav className="flex space-x-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* === ONGLET TAGS === */}
      {activeTab === 'tags' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Plus className="h-5 w-5" />
              {editingTag ? 'Modifier le tag' : 'Ajouter un nouveau tag'}
            </h3>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-800">
                <strong>Note :</strong> Ces couleurs sont utilisées pour les animaux et les propriétaires.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du tag
                </label>
                <input
                  type="text"
                  value={newTag.nom}
                  onChange={(e) => setNewTag({ ...newTag, nom: e.target.value })}
                  placeholder="Ex: Bleu, Rouge, Vert..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-700"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Code couleur
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={newTag.codeHex}
                    onChange={(e) => setNewTag({ ...newTag, codeHex: e.target.value })}
                    className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={newTag.codeHex}
                    onChange={(e) => setNewTag({ ...newTag, codeHex: e.target.value })}
                    placeholder="#2563eb"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ordre d'affichage
                </label>
                <input
                  type="number"
                  value={newTag.ordre}
                  onChange={(e) => setNewTag({ ...newTag, ordre: parseInt(e.target.value) || 1 })}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-700"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aperçu
                </label>
                <div className="h-10 flex items-center">
                  <div
                    className="w-8 h-8 rounded-full border-2 border-gray-300"
                    style={{ backgroundColor: newTag.codeHex }}
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">
                      {newTag.nom || 'Nouveau tag'}
                    </div>
                    <div className="text-xs text-gray-500">
                      ID: {editingTag ? editingTag.id : nextId}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Couleurs rapides
              </label>
              <div className="flex flex-wrap gap-2">
                {presetColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewTag({ ...newTag, codeHex: color })}
                    className={`w-8 h-8 rounded-full border-2 hover:scale-110 transition-transform ${
                      newTag.codeHex === color ? 'border-gray-800' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => handleSaveTag(newTag)}
                disabled={loading || !newTag.nom.trim()}
                className={`px-4 py-2 rounded-md flex items-center gap-2 ${
                  loading || !newTag.nom.trim()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-black text-white hover:bg-gray-800'
                }`}
              >
                <Save className="h-4 w-4" />
                {editingTag ? 'Modifier' : 'Ajouter'}
              </button>
              
              {editingTag && (
                <button
                  onClick={() => {
                    setEditingTag(null);
                    setNewTag({ nom: '', codeHex: '#2563eb', ordre: nextId });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Tags existants ({tagColors.length})
            </h3>
            
            
            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Chargement...</div>
              </div>
            ) : tagColors.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Aucun tag configuré</div>
              </div>
            ) : (
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tagColors.map((tag) => (
                  <div
                    key={tag.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-6 h-6 rounded-full border border-gray-300 flex-shrink-0"
                        style={{ backgroundColor: tag.codeHex }}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{tag.nom}</div>
                        <div className="text-sm text-gray-500">
                          ID: {tag.id} • {tag.codeHex} • Ordre: {tag.ordre}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingTag(tag);
                          setNewTag({ nom: tag.nom, codeHex: tag.codeHex, ordre: tag.ordre });
                        }}
                        className="flex-1 px-3 py-1 text-sm border border-black text-black rounded hover:bg-gray-50"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDeleteTag(tag.id, tag.nom)}
                        className="px-3 py-1 text-sm border border-red-600 text-red-600 rounded hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* === ONGLET INTERVENANTS === */}
      {activeTab === 'intervenants' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Plus className="h-5 w-5" />
              {editingIntervenant ? 'Modifier l\'intervenant' : 'Ajouter un intervenant'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  value={newIntervenant.nom}
                  onChange={(e) => setNewIntervenant({ ...newIntervenant, nom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-700"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom *
                </label>
                <input
                  type="text"
                  value={newIntervenant.prenom}
                  onChange={(e) => setNewIntervenant({ ...newIntervenant, prenom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-700"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <select
                  value={newIntervenant.type}
                  onChange={(e) => setNewIntervenant({ ...newIntervenant, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-700"
                >
                  {typesIntervenants.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={newIntervenant.telephone}
                  onChange={(e) => setNewIntervenant({ ...newIntervenant, telephone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-700"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={newIntervenant.email}
                  onChange={(e) => setNewIntervenant({ ...newIntervenant, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-700"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse
                </label>
                <input
                  type="text"
                  value={newIntervenant.adresse}
                  onChange={(e) => setNewIntervenant({ ...newIntervenant, adresse: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-700"
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newIntervenant.actif}
                  onChange={(e) => setNewIntervenant({ ...newIntervenant, actif: e.target.checked })}
                  className="rounded text-black focus:ring-gray-700"
                />
                <span className="text-sm font-medium text-gray-700">Intervenant actif</span>
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveIntervenant}
                disabled={loading || !newIntervenant.nom.trim() || !newIntervenant.prenom.trim()}
                className={`px-4 py-2 rounded-md flex items-center gap-2 ${
                  loading || !newIntervenant.nom.trim() || !newIntervenant.prenom.trim()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-black text-white hover:bg-gray-800'
                }`}
              >
                <Save className="h-4 w-4" />
                {editingIntervenant ? 'Modifier' : 'Ajouter'}
              </button>
              
              {editingIntervenant && (
                <button
                  onClick={() => {
                    setEditingIntervenant(null);
                    setNewIntervenant({
                      nom: '',
                      prenom: '',
                      type: 'Vétérinaire',
                      telephone: '',
                      email: '',
                      adresse: '',
                      actif: true
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="h-5 w-5" />
              Intervenants ({intervenants.length})
            </h3>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Chargement...</div>
              </div>
            ) : intervenants.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Aucun intervenant configuré</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {intervenants.map((intervenant) => (
                  <div
                    key={intervenant.id}
                    className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                      !intervenant.actif ? 'opacity-60 bg-gray-50' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="font-medium text-gray-900">
                          {intervenant.prenom} {intervenant.nom}
                        </div>
                        <div className="text-sm text-gray-600">
                          {intervenant.type}
                        </div>
                      </div>
                      {!intervenant.actif && (
                        <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded">
                          Inactif
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-1 text-sm text-gray-600 mb-3">
                      {intervenant.telephone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-3 w-3" />
                          {intervenant.telephone}
                        </div>
                      )}
                      {intervenant.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-3 w-3" />
                          {intervenant.email}
                        </div>
                      )}
                      {intervenant.adresse && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3" />
                          {intervenant.adresse}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditIntervenant(intervenant)}
                        className="flex-1 px-3 py-1 text-sm border border-black text-black rounded hover:bg-gray-50"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDeleteIntervenant(intervenant.id, intervenant.nom, intervenant.prenom)}
                        className="px-3 py-1 text-sm border border-red-600 text-red-600 rounded hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      
{/* === ONGLET TYPES D'ACTES === */}
{activeTab === 'actes' && (
  <div className="space-y-6">
    <div className="bg-white rounded-lg border p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <FileText className="h-5 w-5" />
        Configuration des types d'actes
      </h3>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-gray-800">
          <strong>Note :</strong> Cochez les actes que vous souhaitez utiliser. 
          Vous pouvez personnaliser la couleur de chaque catégorie ou de chaque acte individuellement.
        </p>
        {/* Bouton de réinitialisation */}
        <button 
          onClick={async () => {
            if (window.confirm('Voulez-vous réinitialiser tous les types d\'actes ? Cela supprimera les personnalisations.')) {
              try {
                setLoading(true);
                // Supprimer tous les types existants
                const snapshot = await getDocs(collection(db, 'typesActes'));
                const deletePromises = snapshot.docs.map(docSnapshot => deleteDoc(doc(db, 'typesActes', docSnapshot.id)));
                await Promise.all(deletePromises);
                
                // Réinitialiser
                const { initializeTypesActes } = await import('../config/actesConfig');
                await initializeTypesActes(db, setDoc, doc, collection);
                showMessage('success', 'Types d\'actes réinitialisés avec succès !');
                await loadTypesActes();
              } catch (error) {
                console.error('Erreur:', error);
                showMessage('error', 'Erreur lors de la réinitialisation');
              } finally {
                setLoading(false);
              }
            }
          }}
          className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
        >
          Réinitialiser tous les types d'actes
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="text-gray-500">Chargement...</div>
        </div>
      ) : Object.keys(typesActes).length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-500">Aucun type d'acte configuré</div>
          <button 
            onClick={async () => {
              try {
                setLoading(true);
                // Importer la fonction d'initialisation
                const { initializeTypesActes } = await import('../config/actesConfig');
                // Appeler la fonction d'initialisation
                await initializeTypesActes(db, setDoc, doc, collection);
                showMessage('success', 'Types d\'actes initialisés avec succès !');
                // Recharger les types d'actes
                await loadTypesActes();
              } catch (error) {
                console.error('Erreur lors de l\'initialisation:', error);
                showMessage('error', 'Erreur lors de l\'initialisation des types d\'actes');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className={`mt-4 px-4 py-2 rounded-lg hover:bg-gray-800 ${
              loading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-black text-white'
            }`}
          >
            {loading ? 'Initialisation...' : 'Initialiser les types d\'actes'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(typesActes).map(([categorie, actes]) => {
            const allActive = actes.every(a => a.actif);
            const someActive = actes.some(a => a.actif);
            const categoryColor = categoriesColors[categorie] || actes[0]?.couleur || '#6b7280';
            
            return (
              <div key={categorie} className="border rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleCategory(categorie)}
                      className="hover:bg-gray-200 p-1 rounded"
                    >
                      {expandedCategories[categorie] ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                      }
                    </button>
                    
                    <button
                      onClick={() => handleToggleCategory(categorie, allActive)}
                      className="flex items-center"
                      title={allActive ? 'Tout décocher' : 'Tout cocher'}
                    >
                      {allActive ? 
                        <CheckSquare className="h-5 w-5 text-black" /> :
                        someActive ?
                        <Square className="h-5 w-5 text-gray-400" style={{ background: 'linear-gradient(to right, black 50%, white 50%)' }} /> :
                        <Square className="h-5 w-5 text-gray-400" />
                      }
                    </button>
                    
                    <ColorPicker
                      value={categoryColor}
                      onChange={(color) => handleUpdateCategoryColor(categorie, color)}
                      id={`category-${categorie}`}
                    />
                    
                    <span className="font-medium">{categorie}</span>
                    <span className="text-sm text-gray-500">
                      ({actes.filter(a => a.actif).length}/{actes.length} actifs)
                    </span>
                  </div>
                </div>
                
                {expandedCategories[categorie] && (
                  <div className="p-4 space-y-2 bg-white">
                    {actes.map((acte) => (
                      <div
                        key={acte.id}
                        className={`flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 ${
                          !acte.actif ? 'opacity-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={acte.actif}
                          onChange={() => handleToggleActe(acte.id, acte.actif)}
                          className="rounded text-black focus:ring-gray-700"
                        />
                        
                        <ColorPicker
                          value={acte.couleur || categoryColor}
                          onChange={(color) => handleUpdateActeColor(acte.id, color)}
                          id={`acte-${acte.id}`}
                        />
                        
                        <span className="flex-1 text-sm">{acte.nomActe}</span>
                        
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleUpdateActeOrdre(acte.id, 'up')}
                            className="p-1 hover:bg-gray-200 rounded"
                            title="Monter"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleUpdateActeOrdre(acte.id, 'down')}
                            className="p-1 hover:bg-gray-200 rounded"
                            title="Descendre"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
)}

      

      {/* === ONGLET AUTRES === */}
      {activeTab === 'autres' && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Autres paramètres</h3>
          <p className="text-gray-500">
            Espace réservé pour d'autres réglages de l'application.
          </p>
        </div>
      )}
    </PageWrapper>
  );
};

export default ParametresPage;